// What the pop-up bubbles say and when (CollabContext raises them, NudgeToasts draws them):
//   • a new message names who wrote and starts their message, and opens that conversation;
//   • a burst of notifications never stacks past the screen: the newest few, then one "and N more";
//   • a new version refreshes the page only once nothing is being edited, so a half-filled form is never lost.
import type { CollabThread } from './collabHooks.js';
import { isEditableField } from './coopScroll.js';

/** how much of a message the bubble shows. */
export const MESSAGE_PREVIEW_CHARS = 90;
/** notifications shown one bubble each before the rest are summed up in one. */
export const NOTIFICATION_BUBBLES = 2;

const preview = (s: string) => {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > MESSAGE_PREVIEW_CHARS ? `${t.slice(0, MESSAGE_PREVIEW_CHARS - 1).trimEnd()}…` : t;
};

export interface MessageBubble {
  title: string;
  body: string;
  /** the conversation it opens (the newest one with something unread) */
  threadId: string | null;
}

/** the bubble for messages that just arrived: from the conversations with something unread, newest first,
 *  leaving out the one open in the chat panel (it's being read). null when there's nothing to show. With no
 *  conversation list to hand, it falls back to the count. */
export function messageBubble(threads: readonly CollabThread[] | undefined, unread: number, openThread: string | null): MessageBubble | null {
  if (!threads) return unread > 0 ? { title: 'New message', body: `You have ${unread} unread`, threadId: null } : null;
  const waiting = threads
    .filter((t) => t.unread > 0 && t.id !== openThread && t.lastMessage)
    .sort((a, b) => b.lastMessage!.at - a.lastMessage!.at);
  const top = waiting[0];
  if (!top) return null;
  const last = top.lastMessage!;
  const where = top.isGroup && top.title ? ` in ${top.title}` : '';
  const more = waiting.length - 1;
  return {
    title: `${last.fromName || 'New message'}${where}`,
    body: `${last.body ? preview(last.body) : 'Sent a file'}${more > 0 ? ` · and ${more} more conversation${more === 1 ? '' : 's'}` : ''}`,
    threadId: top.id,
  };
}

/** the notifications to give a bubble each, newest first, and how many more are summed up in one bubble. */
export function notificationBubbles<T extends { id: number }>(fresh: readonly T[]): { each: T[]; more: number } {
  const newest = [...fresh].sort((a, b) => b.id - a.id);
  // one left over is shown itself rather than as "and 1 more"
  const each = newest.length <= NOTIFICATION_BUBBLES + 1 ? newest : newest.slice(0, NOTIFICATION_BUBBLES);
  return { each, more: newest.length - each.length };
}

/** whether the page can be refreshed without losing anything: no dialog open and no field in use. */
export function safeToRefresh(doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]')) return false;
  return !isEditableField(doc.activeElement);
}
